import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { SignupForm } from "@lyder-talk/ui";
import { useTranslation } from "react-i18next";
import { getAppUrl, signInWithGoogle } from "@lyder-talk/core";
import { sendDiscordWebhook } from "@/lib/discord-webhook";
import { auth } from "@lyder-talk/core/lib/auth-client";

const Signup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [googleLoading, setGoogleLoading] = useState(false);

  const redirectTo = searchParams.get("redirectTo");
  const redirectQuery = redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : "";

  const handleSubmit = async (email: string, password: string, displayName: string) => {
    try {
      const normalizedEmail = email.toLowerCase().trim();
      const { data, error } = await auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: getAppUrl("/auth/confirm"),
          data: {
            display_name: displayName.trim() || undefined,
          },
        },
      });

      if (error) {
        const alreadyExists = /already registered|already exists/i.test(error.message);
        if (alreadyExists) {
          toast({
            title: t("auth.forms.toasts.accountExists", "Account already exists"),
            description: t("auth.forms.toasts.accountExistsDescription", "You already have an account! Please sign in."),
            variant: "destructive",
          });
          return {
            success: false,
            alreadyExists: true,
            error: t("auth.forms.toasts.accountExistsDescription"),
          };
        }
        throw new Error(error.message);
      }

      void sendDiscordWebhook({
        eventType: "user_signup",
        email: normalizedEmail,
        timestamp: new Date().toISOString(),
      });

      if (data?.session) {
        toast({ title: t("auth.forms.toasts.welcome", "Welcome!") });
      } else {
        toast({
          title: t("auth.modal.toasts.checkEmail", "Check your email"),
          description: t("auth.modal.toasts.checkEmailDescription", "Please confirm your email to activate your account."),
        });
      }

      return { success: true, requiresEmailConfirmation: !data?.session };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("common.errors.generic");
      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: message,
        variant: "destructive",
      });
      return { success: false, error: message };
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch {
      toast({
        title: t("auth.forms.toasts.error", "Error"),
        description: t("auth.forms.toasts.googleFailed", "Failed to sign up with Google"),
        variant: "destructive",
      });
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <SignupForm
      onSubmit={handleSubmit}
      onNavigateToSignin={() => navigate(`/auth/signin${redirectQuery}`)}
      onGoogleSignIn={handleGoogle}
      googleLoading={googleLoading}
      // Injected rather than read from i18next inside @lyder-talk/ui — see the
      // note on `copy` there about the admin panel sharing these forms.
      copy={{
        showPasswordLabel: t("auth.forms.signup.showPassword"),
        hidePasswordLabel: t("auth.forms.signup.hidePassword"),
        passwordStrengthLabels: [
          t("auth.passwordStrength.label", "Strength"),
          t("auth.passwordStrength.weak", "Weak"),
          t("auth.passwordStrength.fair", "Fair"),
          t("auth.passwordStrength.strong", "Strong"),
          t("auth.passwordStrength.excellent", "Excellent"),
        ],
        passwordRuleLabels: [
          t("auth.passwordRules.characters", "8+ characters"),
          t("auth.passwordRules.number", "a number"),
          t("auth.passwordRules.capital", "a capital letter"),
        ],
        successTitle: t("auth.forms.signup.successTitle"),
        successDescription: t("auth.forms.signup.successDescription"),
        successTitleConfirmEmail: t("auth.forms.signup.successTitleConfirmEmail"),
        successDescriptionConfirmEmail: t("auth.forms.signup.successDescriptionConfirmEmail"),
        steps: [
          t("auth.forms.signup.steps.0"),
          t("auth.forms.signup.steps.1"),
          t("auth.forms.signup.steps.2"),
        ],
        successActionConfirmEmail: t("auth.forms.signup.successActionConfirmEmail"),
        successAction: t("auth.forms.signup.successAction"),
        title: t("auth.forms.signup.title"),
        description: t("auth.forms.signup.description"),
        displayNameLabel: t("auth.forms.signup.displayNameLabel"),
        displayNamePlaceholder: t("auth.forms.signup.displayNamePlaceholder"),
        emailLabel: t("auth.forms.signup.emailLabel"),
        passwordLabel: t("auth.forms.signup.passwordLabel"),
        confirmPasswordLabel: t("auth.forms.signup.confirmPasswordLabel"),
        matchHint: t("auth.forms.signup.matchHint"),
        creating: t("auth.forms.signup.creating"),
        submit: t("auth.forms.signup.submit"),
        googleLabel: t("auth.forms.signup.googleLabel"),
        passwordTooShort: t("auth.forms.signup.passwordTooShort"),
        passwordsMismatch: t("auth.forms.signup.passwordsMismatch"),
        unexpectedError: t("auth.forms.signup.unexpectedError"),
        haveAccount: t("auth.forms.signup.haveAccount"),
        signInInstead: t("auth.forms.signup.signInInstead"),
        legal: {
          termsPrefix: t("auth.forms.legal.termsPrefix"),
          termsLink: t("auth.forms.legal.termsLink"),
          andPrefix: t("auth.forms.legal.andPrefix"),
          privacyLink: t("auth.forms.legal.privacyLink"),
          orContinueWith: t("auth.forms.legal.orContinueWith"),
        },
      }}
    />
  );
};

export default Signup;
